import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService, CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  FormGroupComponent,
  MbsPopupType,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  RadioComponent,
  RadioDescription,
  ToastService
} from 'mbs-ui-kit';
import { TFARequireModalComponent } from 'mbs-ui-shared@components';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { MANUAL_ERROR_HANDLED } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import { ComputerPlanBasedModal } from 'mbs-ui-shared@models/ComputersModals';
import { PlanInfo } from 'mbs-ui-shared@models/backup/plan-info-model';
import { RemoteCommandType } from 'mbs-ui-shared@models/rm/remote-command-model';
import { AsyncRemoteCommandService } from 'mbs-ui-shared@services/async-remote-command/async-remote-command.service';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';
import { finalize } from 'rxjs/operators';

enum DeletionMode {
  WithData,
  WithoutData
}

@UntilDestroy()
@Component({
  selector: 'mbs-delete-plan-with-data-modal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ReactiveFormsModule,
    NgbTooltipModule,
    CanPipe,
    FormGroupComponent,
    AlertComponent,
    ModalComponent,
    I18NextPipe,
    RadioComponent,
    RadioDescription,
    ModalFooterDirective
  ],
  templateUrl: './delete-plan-with-data-modal.component.html'
})
export class DeletePlanWithDataModalComponent implements ComputerPlanBasedModal, OnInit {
  private facade = inject(ComputerBackupFacade);
  private computersFacade = inject(ComputersFacade);
  private toast = inject(ToastService);
  private i18N = inject(I18NextPipe);
  private ability = inject(AbilityService);
  private modalService = inject(ModalService);
  private tfaService = inject(TFAService);
  private asyncRemoteCommandService = inject(AsyncRemoteCommandService);

  public readonly permissionsEnum = PermissionsEnum;
  public readonly mbsPopupType = MbsPopupType;

  public computer: Computer;
  public plan: PlanInfo;
  public loading = false;
  public deletionType = new FormControl(DeletionMode.WithoutData);
  public error: {
    message: string;
    details?: string[];
  } = null;

  @ViewChild(ModalComponent, { static: true }) private baseModal;

  ngOnInit() {
    this.deletionType.valueChanges.pipe(untilDestroyed(this)).subscribe(() => (this.error = null));
  }

  public onDeleteButtonClick() {
    this.deletionType?.value === DeletionMode.WithData ? this.deletePlanWithData() : this.deletePlanWithoutData();
  }

  private deletePlanWithData() {
    if (!this.ability.can('read', '2FAEnabled')) {
      return void this.modalService.openCustom(TFARequireModalComponent, {
        size: MbsSize.sm,
        container: 'body',
        data: {
          actionName: this.i18N.transform('shared:computers.modals.deletePlanCaption')
        }
      });
    }

    const query$ = this.asyncRemoteCommandService.runAsyncCommand({
      hid: this.computer.hid,
      params: {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.DeleteBackupAndPlan,
        params: { planIdToDelete: this.plan?.id }
      },
      options: {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          MANUAL_ERROR_HANDLED
        }
      }
    });

    this.loading = true;
    this.tfaService
      .handleTFA(query$)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.computersFacade.loadComputerByHid({ hid: this.computer?.hid, force: true });
          this.handleSuccessRemoving();
        },
        error: ({ error }) => {
          if (!error?.detail) return;

          error.detail = error.detail.endsWith('.') ? error.detail.substring(0, error.detail.length - 1) + ':' : error.detail + ':';

          this.error = {
            message: error.detail,
            details: error?.errorList?.map((error) => error.detail)
          };
        }
      });
  }

  private deletePlanWithoutData() {
    this.loading = true;

    this.facade
      .removePlan(this.computer, this.plan?.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(() => {
        this.handleSuccessRemoving();
      });
  }

  private handleSuccessRemoving() {
    this.facade.loadPlans({ computer: this.computer, force: true });
    this.toast.success(this.i18N.transform('shared:computers.modals.deletePlanSuccessMessage', { planName: this.plan?.displayName }));
    this.baseModal.close();
  }
}
